import React, { useEffect, useRef, useState } from 'react';
import './AffirmationsAnimation.css';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

const AffirmationsAnimation = ({ affirmations, userName, userEmail, waitTime, scrollSpeed = 6, onAudioUrlReceived, contactId, chatHistory, chatSummary }) => {
  const ribbonRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const hasFetchedAudioUrl = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const ribbon = ribbonRef.current;
    const sentences = [...affirmations, ...affirmations, ...affirmations, ...affirmations];

    sentences.forEach((sentence) => {
      const div = document.createElement('div');
      div.className = 'sentence';
      div.innerText = sentence;
      ribbon.appendChild(div);
    });

    let animationFrameId;
    let startTime;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;

      const loadingProgress = (elapsedTime / waitTime) * 100;
      setProgress(Math.min(loadingProgress, 100));

      const totalScrollDistance = scrollSpeed * ribbon.offsetHeight / sentences.length;
      const scrollAmount = (elapsedTime * totalScrollDistance / waitTime) % ribbon.offsetHeight;
      ribbon.style.transform = `translateY(-${scrollAmount}px)`;

      if (elapsedTime < waitTime) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      while (ribbon.firstChild) {
        ribbon.removeChild(ribbon.firstChild);
      }
      cancelAnimationFrame(animationFrameId);
    };
  }, [affirmations, waitTime, scrollSpeed]);

  useEffect(() => {
    if (hasFetchedAudioUrl.current || isLoading) return;

    const fetchAudioUrl = async () => {
      try {
        const previewId = uuid();
        console.log('Generated preview_id:', previewId);

        const response = await axios.post(`${backendUrl}/api/generate_audio`, {
          affirmations: affirmations,
          user_name: userName,
          user_email: userEmail,
          contact_id: contactId,
          context: "preview",
          chat_history: chatHistory,
          chat_summary: chatSummary,
          preview_id: previewId
        }, {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true,
          timeout: 90000
        });

        if (response.status === 202) {
          const jobId = response.data.job_id;
          console.log('Job started:', jobId);

          const pollStatus = async () => {
            try {
              const statusResponse = await axios.get(`${backendUrl}/api/task_status/${jobId}`, {
                withCredentials: true
              });

              if (statusResponse.data.status === 'completed') {
                const s3Url = statusResponse.data.s3_url;
                const pId = statusResponse.data.preview_id || previewId;
                console.log('Job completed. S3 URL:', s3Url);

                // Fetch the actual audio blob from S3
                const audioResponse = await axios.get(s3Url, {
                  responseType: 'blob'
                });

                hasFetchedAudioUrl.current = true;
                sessionStorage.setItem('preview_id', pId);
                onAudioUrlReceived(audioResponse.data);
              } else if (statusResponse.data.status === 'failed') {
                throw new Error('Audio generation failed: ' + (statusResponse.data.error || 'Unknown error'));
              } else {
                // Still processing, poll again in 2 seconds
                setTimeout(pollStatus, 2000);
              }
            } catch (error) {
              console.error('Error polling status:', error);
            }
          };

          pollStatus();
        } else {
          throw new Error('Error starting audio generation');
        }
      } catch (error) {
        console.error('Error fetching audio URL:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudioUrl();
  }, [backendUrl, affirmations, userName, userEmail, onAudioUrlReceived, contactId, chatHistory, chatSummary, isLoading]);

  return (
    <div>
      <div className="logo">
        <img src="ML_Logo_Hor_White.png" alt="ManifestLab Logo" className="logo-img" />
      </div>
      <div className="container-affirmations">
        <div className="heading">
          <div className="user-name">One Moment, {userName}!</div>
          <div className="generating-text">Generating your personalized meditation...</div>
        </div>
        <div className="progress-container">
          <div
            className="progress-text"
            style={{ left: `${progress}%` }}
          >
            {`${Math.round(progress)}%`}
          </div>
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        <div className="affirmations-animation">
          <div className="ribbon" ref={ribbonRef}></div>
        </div>
      </div>
    </div>
  );
};

export default AffirmationsAnimation;